/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';
import { LinkItem } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import {
	RenderTracker,
	SWITCHER_ITEM_LOZENGES_SUBJECT,
	type WithAnalyticsEventsProps,
} from '../../common/utils/analytics';
import { FadeIn } from './fade-in';
import { cssMap, jsx } from '@compiled/react';
import Lozenge from '@atlaskit/lozenge';
import { FormattedMessage, type MessageDescriptor } from 'react-intl-next';
import messages from './messages';
import ErrorBoundary from '../components/error-boundary';
import { Box } from '@atlaskit/primitives/compiled';
import { useAnalyticsUrlWrapper } from '../../common/utils/use-analytics-url-wrapper';
import { useUnifiedAnalyticsContext } from '../../common/providers/unified-analytics-data-provider';
import { useUnifiedAnalytics } from '../../common/utils/analytics/use-unified-analytics';

export interface SwitcherItemProps extends WithAnalyticsEventsProps {
	children: React.ReactNode;
	className?: string;
	description?: React.ReactNode;
	disableAnimation?: boolean;
	elemAfter?: React.ReactNode;
	href?: string;
	icon: React.ReactNode;
	isDisabled?: boolean;
	isDraggable?: boolean;
	isHovered?: boolean;
	isOverflowButton?: boolean;
	lozenges?: LozengeType[];
	onClick?: (e: React.SyntheticEvent) => void;
	onContextMenu?: (e: React.SyntheticEvent) => void;
	onKeyDown?: any;
	product?: string;
	reorderButtonIsVisible?: boolean;
	setIsLinkItemHovered?: (hovered: boolean) => void;
	shouldAllowMultiline?: boolean;
	shouldShowNewLozenge?: boolean;
	tag?: keyof JSX.IntrinsicElements;
	target?: string;
	testId?: string;
}

export enum LozengeType {
	New = 'new',
	Try = 'try',
}

export interface LozengeConfig {
	componentProps: React.ComponentProps<typeof Lozenge>;
	label: MessageDescriptor;
}

const lozengeData: Record<LozengeType, LozengeConfig> = {
	[LozengeType.New]: {
		label: messages.newLozenge,
		componentProps: {
			appearance: 'new',
		},
	},
	[LozengeType.Try]: {
		label: messages.tryLozenge,
		componentProps: {
			appearance: 'new',
		},
	},
};

const styles = cssMap({
	fadeIn: {
		width: '100%',
	},
	itemDescription: {
		display: 'block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginRight: token('space.400'),
	},
	hoveredDescription: {
		marginRight: token('space.800'),
	},
	titleWrapper: {
		display: 'block',
	},
	overflowTitleWrapper: {
		width: '70%',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	hasDescription: {
		marginTop: token('space.negative.025'),
		marginBottom: token('space.negative.025'),
	},
});

const SwitcherItem = React.forwardRef<HTMLAnchorElement, SwitcherItemProps>(
	(
		{
			icon,
			description,
			elemAfter,
			onClick,
			children,
			lozenges,
			product,
			tag,
			href,
			isDraggable,
			isHovered,
			setIsLinkItemHovered,
			disableAnimation,
			isOverflowButton,
			onContextMenu,
			...rest
		},
		focusRef,
	) => {
		const cursorTimeoutRef = React.useRef<number | null>(null);
		const { productSwitcher } = useUnifiedAnalyticsContext();
		const { fireMenuStateChanged } = useUnifiedAnalytics();
		const getAnalyticsWrappedURL = useAnalyticsUrlWrapper(productSwitcher ?? 'appSwitcher');
		const analyticsWrappedURL = href ? getAnalyticsWrappedURL(href) : href;

		// Cleanup timeout on unmount
		React.useEffect(() => {
			return () => {
				if (cursorTimeoutRef.current) {
					clearTimeout(cursorTimeoutRef.current);
				}
			};
		}, []);

		const itemDescription = React.useMemo<JSX.Element | string | undefined>(() => {
			if (React.isValidElement(description) || typeof description === 'string') {
				return (
					<span css={[styles.itemDescription, isHovered && styles.hoveredDescription]}>
						{description}
					</span>
				);
			}
			return undefined;
		}, [description, isHovered]);

		const renderLozenges = React.useMemo<JSX.Element | null>(() => {
			if (!lozenges?.length) {
				return null;
			}

			return (
				<ErrorBoundary
					product={product}
					triggerSubject="switcherItemLozengeErrorBoundary"
					hideFallbackUI
				>
					<>
						{lozenges.map((lozenge, index) => {
							const { componentProps, label } = lozengeData[lozenge];
							return (
								<span
									key={index}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={{ marginLeft: token('space.100') }}
								>
									<Lozenge {...componentProps} testId="switcher-item-lozenge">
										<FormattedMessage {...label} />
									</Lozenge>
								</span>
							);
						})}
						<RenderTracker
							subject={SWITCHER_ITEM_LOZENGES_SUBJECT}
							data={{
								product,
								lozenges,
								children,
								description,
							}}
						/>
					</>
				</ErrorBoundary>
			);
		}, [lozenges, product, children, description]);

		return (
			<FadeIn disableAnimation={disableAnimation} css={styles.fadeIn} tag={tag}>
				<Box
					onMouseEnter={(e: React.MouseEvent) => {
						setIsLinkItemHovered?.(true);
						if (e.target && isDraggable) {
							// Clear any existing timeout
							if (cursorTimeoutRef.current) {
								clearTimeout(cursorTimeoutRef.current);
							}
							// Set new timeout
							cursorTimeoutRef.current = window.setTimeout(() => {
								(e.target as HTMLElement).style.cursor = 'grab';
								cursorTimeoutRef.current = null;
							}, 800);
						}
					}}
					onMouseLeave={(e: React.MouseEvent) => {
						setIsLinkItemHovered?.(false);
						if (e.target && isDraggable) {
							// Clear timeout if still pending
							if (cursorTimeoutRef.current) {
								clearTimeout(cursorTimeoutRef.current);
								cursorTimeoutRef.current = null;
							}
							// Reset cursor
							(e.target as HTMLElement).style.cursor = '';
						}
					}}
					onContextMenu={(event: React.MouseEvent) => {
						if (onContextMenu) {
							onContextMenu(event);
						}
					}}
				>
					<LinkItem
						testId="switcher-base-item"
						description={itemDescription}
						iconBefore={icon}
						shouldTitleWrap={false}
						shouldDescriptionWrap={false}
						iconAfter={elemAfter}
						target={rest.target ?? '_blank'}
						href={analyticsWrappedURL ?? '#'}
						onClick={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
							// To prevent the page from reloading when using a fallback '#' in href, make sure to use preventDefault.
							if (!href) {
								event.preventDefault();
							}
							if (onClick) {
								onClick(event);
							}
							// this event should only be fired when clicking on an item closes the switcher menu
							if (!isOverflowButton) {
								fireMenuStateChanged({ itemState: 'taskCompleted' });
							}
						}}
						ref={focusRef}
						{...rest}
					>
						<span
							css={[
								styles.titleWrapper,
								itemDescription && styles.hasDescription,
								isDraggable && isHovered && styles.overflowTitleWrapper,
							]}
						>
							{children}
							{renderLozenges}
						</span>
					</LinkItem>
				</Box>
			</FadeIn>
		);
	},
);

export default SwitcherItem;
